/**
 * Register an existing QIE zone as an EHR connection in this environment's
 * registry. The zone name is checked with checkEhrZone first, and Register
 * stays disabled until the check passes for the name as typed. VITALL only.
 */
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useListAccounts } from '@hooks/api/account';
import {
  type EhrZoneCheck,
  useEhrConnectionRegistration,
  useEhrZoneCheck,
} from '@hooks/api/ehr-sync-registry';

import {
  AccountType,
  EhrEnvironment,
  EhrVendor,
  EhrZoneCheckResult,
} from 'generated/types';

import { ehrEnvironmentMessages } from '../messages';

const messages = defineMessages({
  title: {
    id: 'ehrSync.register.title',
    defaultMessage: 'Register EHR connection',
  },
  intro: {
    id: 'ehrSync.register.intro',
    defaultMessage:
      "Adds an existing QIE zone to this environment's registry. Only registered zones appear on the Integrations page.",
  },
  organization: {
    id: 'ehrSync.register.organization',
    defaultMessage: 'Organization',
  },
  zoneName: {
    id: 'ehrSync.register.zoneName',
    defaultMessage: 'QIE zone name',
  },
  zoneHelper: {
    id: 'ehrSync.register.zoneHelper',
    defaultMessage:
      'Convention: {convention}. The name is stored with the zone id so a rename shows as a mismatch.',
  },
  checkZone: { id: 'ehrSync.register.checkZone', defaultMessage: 'Check zone' },
  found: {
    id: 'ehrSync.register.found',
    defaultMessage:
      'Zone found in QIE · zone id {id} · {count, plural, one {# channel} other {# channels}}',
  },
  notFound: {
    id: 'ehrSync.register.notFound',
    defaultMessage:
      'No zone named {name} in QIE. Check the name in the QIE console.',
  },
  outside: {
    id: 'ehrSync.register.outside',
    defaultMessage:
      '{name} is not a zone for this environment. Its name must start with {prefix}.',
  },
  alreadyRegistered: {
    id: 'ehrSync.register.alreadyRegistered',
    defaultMessage: 'Zone {name} is already registered to {organization}.',
  },
  alreadyRegisteredUnknown: {
    id: 'ehrSync.register.alreadyRegisteredUnknown',
    defaultMessage: 'Zone {name} is already registered.',
  },
  unreachable: {
    id: 'ehrSync.register.unreachable',
    defaultMessage:
      "Couldn't reach QIE to check the zone. Registration needs QIE to be up. Try again in a moment.",
  },
  vendor: { id: 'ehrSync.register.vendor', defaultMessage: 'EHR vendor' },
  fhirVersion: {
    id: 'ehrSync.register.fhirVersion',
    defaultMessage: 'FHIR version',
  },
  fhirVersionHelper: {
    id: 'ehrSync.register.fhirVersionHelper',
    defaultMessage: 'Only R4 is supported.',
  },
  environment: {
    id: 'ehrSync.register.environment',
    defaultMessage: 'EHR environment',
  },
  environmentHelper: {
    id: 'ehrSync.register.environmentHelper',
    defaultMessage:
      'The EHR system this connection talks to. Separate from the Engage environment.',
  },
  fhirBaseUrl: {
    id: 'ehrSync.register.fhirBaseUrl',
    defaultMessage: 'FHIR base URL',
  },
  fhirBaseUrlInvalid: {
    id: 'ehrSync.register.fhirBaseUrlInvalid',
    defaultMessage: 'Enter an http or https URL with a full host name.',
  },
  network: { id: 'ehrSync.register.network', defaultMessage: 'Network' },
  networkHelper: {
    id: 'ehrSync.register.networkHelper',
    defaultMessage: 'Set when a network runs more than one EHR.',
  },
  startsProvisioning: {
    id: 'ehrSync.register.startsProvisioning',
    defaultMessage: 'Starts as Provisioning until its channels run.',
  },
  cancel: { id: 'ehrSync.register.cancel', defaultMessage: 'Cancel' },
  register: { id: 'ehrSync.register.submit', defaultMessage: 'Register' },
});

const VENDORS: EhrVendor[] = [
  EhrVendor.Epic,
  EhrVendor.Cerner,
  EhrVendor.Other,
];

const ENVIRONMENTS = [
  { value: EhrEnvironment.Sandbox, label: ehrEnvironmentMessages.sandbox },
  { value: EhrEnvironment.NonProd, label: ehrEnvironmentMessages.nonProd },
  {
    value: EhrEnvironment.Production,
    label: ehrEnvironmentMessages.production,
  },
];

/**
 * Whether a string is an http(s) URL the backend's `@IsUrl()` will accept. Its
 * defaults require a dotted host (so no `localhost`) and no whitespace, and
 * reject underscores in the host.
 */
const isHttpUrl = (value: string) => {
  const trimmed = value.trim();
  if (/\s/.test(trimmed)) {
    return false;
  }
  try {
    const url = new URL(trimmed);
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      url.hostname.includes('.') &&
      !url.hostname.includes('_')
    );
  } catch {
    return false;
  }
};

/**
 * A zone check outcome for one name: a result from the backend, QIE being
 * unreachable, or another failure with its message.
 */
type CheckState =
  | { zoneName: string; check: EhrZoneCheck }
  | { zoneName: string; unreachable: true }
  | { zoneName: string; failure: string }
  | null;

/** Props for {@link RegisterConnectionDialog}. */
interface RegisterConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful registration, with the zone name. */
  onRegistered: (zoneName: string) => void;
}

/**
 * Register connection dialog.
 *
 * @param props - see {@link RegisterConnectionDialogProps}
 */
export default function RegisterConnectionDialog({
  open,
  onClose,
  onRegistered,
}: RegisterConnectionDialogProps) {
  const { formatMessage } = useIntl();
  const { data: accounts = [], loading: accountsLoading } = useListAccounts(
    AccountType.organization
  );
  const [checkZone, checking] = useEhrZoneCheck();
  const [register, registering] = useEhrConnectionRegistration();

  const [accountId, setAccountId] = useState<string | null>(null);
  const [zoneName, setZoneName] = useState('');
  const [checkState, setCheckState] = useState<CheckState>(null);
  const [vendor, setVendor] = useState<EhrVendor>(EhrVendor.Epic);
  const [environment, setEnvironment] = useState<EhrEnvironment | null>(null);
  const [fhirBaseUrl, setFhirBaseUrl] = useState('');
  const [network, setNetwork] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset on open, not on close, so the fields do not clear mid-fade.
  useEffect(() => {
    if (open) {
      setAccountId(null);
      setZoneName('');
      setCheckState(null);
      setVendor(EhrVendor.Epic);
      setEnvironment(null);
      setFhirBaseUrl('');
      setNetwork('');
      setSubmitError(null);
    }
  }, [open]);

  const trimmedZone = zoneName.trim();
  // A check only counts for the exact name it was run on.
  const currentCheck =
    checkState && checkState.zoneName === trimmedZone ? checkState : null;
  const zoneOk =
    currentCheck !== null &&
    'check' in currentCheck &&
    currentCheck.check.result === EhrZoneCheckResult.Found;
  const fhirBaseUrlInvalid = Boolean(fhirBaseUrl) && !isHttpUrl(fhirBaseUrl);
  const canRegister =
    Boolean(accountId) &&
    zoneOk &&
    environment !== null &&
    isHttpUrl(fhirBaseUrl) &&
    !registering;

  const runCheck = async () => {
    setSubmitError(null);
    const name = trimmedZone;
    const { check, error } = await checkZone(name);
    if (check) {
      setCheckState({ zoneName: name, check });
    } else if (error) {
      // Only a failed request or a 503 means QIE is down; anything else (a
      // validation error, Forbidden) is shown as the backend worded it.
      const down =
        Boolean(error.networkError) ||
        error.graphQLErrors.some(
          // formatGraphqlError puts the HTTP status on the error as `code`
          // and sends no `extensions`, despite the GraphQLError type.
          (e) => {
            const raw = e as {
              code?: unknown;
              extensions?: { code?: unknown };
            };
            return raw.code === '503' || raw.extensions?.code === '503';
          }
        );
      setCheckState(
        down
          ? { zoneName: name, unreachable: true }
          : { zoneName: name, failure: error.message }
      );
    }
  };

  const submit = async () => {
    if (!accountId || !environment) {
      return;
    }
    setSubmitError(null);
    const { error } = await register({
      accountId,
      zoneName: trimmedZone,
      vendor,
      environment,
      fhirBaseUrl,
      network,
    });
    if (error) {
      // 409 and 422 carry a message written for this dialog.
      setSubmitError(error.message);
      return;
    }
    onRegistered(trimmedZone);
    onClose();
  };

  const checkMessage = (): {
    severity: 'success' | 'error';
    text: string;
  } | null => {
    if (!currentCheck) {
      return null;
    }
    if ('unreachable' in currentCheck) {
      return { severity: 'error', text: formatMessage(messages.unreachable) };
    }
    if ('failure' in currentCheck) {
      return { severity: 'error', text: currentCheck.failure };
    }
    const { check } = currentCheck;
    const name = check.zoneName;
    switch (check.result) {
      case EhrZoneCheckResult.Found:
        return {
          severity: 'success',
          text: formatMessage(messages.found, {
            id: check.zoneId,
            count: check.channelCount ?? 0,
          }),
        };
      case EhrZoneCheckResult.NotFound:
        return {
          severity: 'error',
          text: formatMessage(messages.notFound, { name }),
        };
      case EhrZoneCheckResult.OutsideEnvironment:
        return {
          severity: 'error',
          text: formatMessage(messages.outside, {
            name,
            prefix: check.zonePrefix ?? '',
          }),
        };
      case EhrZoneCheckResult.AlreadyRegistered:
        return {
          severity: 'error',
          text: check.registeredAccountName
            ? formatMessage(messages.alreadyRegistered, {
                name,
                organization: check.registeredAccountName,
              })
            : formatMessage(messages.alreadyRegisteredUnknown, { name }),
        };
      default:
        return null;
    }
  };
  const zoneMessage = checkMessage();

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!registering) {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      aria-labelledby="ehr-register-title"
      aria-describedby="ehr-register-intro"
    >
      <DialogTitle id="ehr-register-title">
        {formatMessage(messages.title)}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="ehr-register-intro" variant="body2">
          {formatMessage(messages.intro)}
        </DialogContentText>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Autocomplete
            options={accounts}
            loading={accountsLoading}
            value={accounts.find((a) => a.account_id === accountId) ?? null}
            onChange={(_event, value) =>
              setAccountId(value?.account_id ?? null)
            }
            getOptionLabel={(option) =>
              option.account_name ?? option.account_id
            }
            isOptionEqualToValue={(option, value) =>
              option.account_id === value.account_id
            }
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label={formatMessage(messages.organization)}
              />
            )}
          />

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                required
                fullWidth
                label={formatMessage(messages.zoneName)}
                value={zoneName}
                onChange={(event) => setZoneName(event.target.value)}
                helperText={formatMessage(messages.zoneHelper, {
                  convention: '{env}-{accountId}-{vendor}',
                })}
                inputProps={{ spellCheck: false, maxLength: 200 }}
                sx={{ '& input': { fontFamily: 'monospace' } }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  void runCheck();
                }}
                disabled={!trimmedZone || checking}
                sx={{ mt: 1, whiteSpace: 'nowrap' }}
              >
                {formatMessage(messages.checkZone)}
              </Button>
            </Stack>
            {zoneMessage ? (
              <Alert
                severity={zoneMessage.severity}
                role={zoneMessage.severity === 'error' ? 'alert' : 'status'}
              >
                {zoneMessage.text}
              </Alert>
            ) : null}
          </Stack>

          <TextField
            select
            required
            label={formatMessage(messages.vendor)}
            value={vendor}
            onChange={(event) => setVendor(event.target.value as EhrVendor)}
          >
            {VENDORS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={formatMessage(messages.fhirVersion)}
            value="R4"
            disabled
            helperText={formatMessage(messages.fhirVersionHelper)}
          />

          <Stack spacing={0.75}>
            <Typography
              variant="body2"
              fontWeight="bold"
              id="ehr-environment-label"
            >
              {formatMessage(messages.environment)}
              <span aria-hidden> *</span>
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={environment}
              onChange={(_event, value: EhrEnvironment | null) => {
                if (value) {
                  setEnvironment(value);
                }
              }}
              aria-labelledby="ehr-environment-label"
              aria-describedby="ehr-environment-helper"
            >
              {ENVIRONMENTS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {formatMessage(option.label)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography
              variant="caption"
              color="text.secondary"
              id="ehr-environment-helper"
            >
              {formatMessage(messages.environmentHelper)}
            </Typography>
          </Stack>

          <TextField
            required
            label={formatMessage(messages.fhirBaseUrl)}
            value={fhirBaseUrl}
            onChange={(event) => setFhirBaseUrl(event.target.value)}
            error={fhirBaseUrlInvalid}
            helperText={
              fhirBaseUrlInvalid
                ? formatMessage(messages.fhirBaseUrlInvalid)
                : undefined
            }
            inputProps={{ spellCheck: false }}
          />

          <TextField
            label={formatMessage(messages.network)}
            value={network}
            onChange={(event) => setNetwork(event.target.value)}
            helperText={formatMessage(messages.networkHelper)}
          />

          {submitError ? (
            <Alert severity="error" role="alert">
              {submitError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: 1, pl: 2 }}
        >
          {formatMessage(messages.startsProvisioning)}
        </Typography>
        <Button onClick={onClose} disabled={registering}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          variant="contained"
          disabled={!canRegister}
          onClick={() => {
            void submit();
          }}
        >
          {formatMessage(messages.register)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
